import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button, Card, Col, DatePicker, Empty, Row, Select, Space, Statistic,
  Table, Tag, Typography, message
} from 'antd';
import {
  ReloadOutlined, PrinterOutlined, DownloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { ErpMasterShell } from '../../../../components/common/erp-grid';
import { accountReportsApi } from './accountReportsApi';
import {
  ACCOUNT_TYPE_COLOR, ACCOUNT_TYPE_LABEL, VOUCHER_TYPE_LABEL,
  formatINR, shortDate
} from './accountReportsConstants';
// Stage 6B.9F — friendly error / empty / retry helpers.
import { Alert } from 'antd';
import {
  friendlyAccountsErrorMessage, friendlyAccountsEmptyMessage
} from '../accountsPageState';
import '../accounts.css';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Ledger Statement page (Stage 6B.5).
//
// Pick a ledger + optional date range → shows every posted voucher
// line that touches that ledger, with a running balance.
// Opening + Closing balances surface in a header card and follow the
// debit-positive sign convention (`current_balance = debit - credit`).
// Credit-normal ledgers show the closing magnitude with a "Cr" tag
// instead of a confusing negative number.
// ─────────────────────────────────────────────────────────────────

function csvSafe(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function downloadCsv(filename, rows, columns) {
  const header = columns.map((c) => csvSafe(c.title)).join(',');
  const body = rows.map((r) =>
    columns.map((c) => csvSafe(typeof c.value === 'function' ? c.value(r) : r[c.dataIndex])).join(',')
  ).join('\n');
  const blob = new Blob([header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Searchable ledger Select with server-mode lookup. Same pattern as
// the Journal Voucher modal but standalone so this page doesn't
// import the modal's internal component.
function LedgerPicker({ value, onChange }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();

  const fetchList = useCallback(async (q) => {
    setLoading(true);
    try {
      const rows = await accountReportsApi.ledgerLookup(q || '', 100);
      setOptions(rows);
    } catch (_e) { /* swallow — UI shows empty */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchList(''); }, [fetchList]);

  const onSearch = useCallback((text) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchList(text), 200);
  }, [fetchList]);

  return (
    <Select
      style={{ width: 340 }}
      placeholder="Select ledger…"
      showSearch
      value={value || undefined}
      onChange={(v) => onChange(v)}
      onSearch={onSearch}
      filterOption={false}
      loading={loading}
      notFoundContent={loading ? 'Loading…' : 'No ledgers'}
      options={options.map((o) => ({
        value: o.id,
        label: (
          <span>
            <code style={{ marginRight: 6, fontSize: 11, color: '#888' }}>{o.accountCode}</code>
            {o.accountName}
            <span style={{ marginLeft: 6, fontSize: 11, color: '#999' }}>· {o.accountType}</span>
          </span>
        )
      }))}
    />
  );
}

export default function LedgerStatementPage() {
  const [ledgerId, setLedgerId] = useState(null);
  const [dateRange, setDateRange] = useState(null); // [dayjs, dayjs]
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Stage 6B.9F — persist the last error so the page can render a
  // Retry Alert instead of relying on a transient toast.
  const [loadError, setLoadError] = useState(null);

  const fetchReport = useCallback(async () => {
    if (!ledgerId) { setData(null); setLoadError(null); return; }
    setLoading(true);
    setLoadError(null);
    try {
      const out = await accountReportsApi.ledgerStatement({
        ledgerAccountId: ledgerId,
        fromDate: dateRange?.[0]?.format('YYYY-MM-DD') || null,
        toDate:   dateRange?.[1]?.format('YYYY-MM-DD') || null
      });
      setData(out);
    } catch (e) {
      setLoadError(e);
      message.error(friendlyAccountsErrorMessage(e, 'ledger_statement'));
    } finally { setLoading(false); }
  }, [ledgerId, dateRange]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const ledger = data?.ledger;
  const rows = data?.rows || [];
  const summary = data?.summary || {
    openingBalance: 0, totalDebit: 0, totalCredit: 0,
    closingBalance: 0, closingSide: 'Nil', lineCount: 0
  };

  const closingDisplay = useMemo(() => {
    const cb = summary.closingBalance;
    if (cb === 0) return { value: 0, suffix: '' };
    return { value: Math.abs(cb), suffix: summary.closingSide };
  }, [summary]);

  const columns = useMemo(() => ([
    { title: 'Date', dataIndex: 'voucherDate', key: 'voucherDate', width: 110,
      render: shortDate },
    { title: 'Voucher No', dataIndex: 'voucherNo', key: 'voucherNo', width: 160,
      render: (v) => <code style={{ fontSize: 12 }}>{v}</code> },
    { title: 'Type', dataIndex: 'voucherType', key: 'voucherType', width: 100,
      render: (v) => <Tag color="blue">{VOUCHER_TYPE_LABEL[v] || v}</Tag> },
    { title: 'Narration', key: 'narration',
      render: (_v, r) => r.lineNarration || r.narration ||
        <Text type="secondary">—</Text> },
    { title: 'Debit (₹)', dataIndex: 'debitAmount', key: 'debit', width: 130, align: 'right',
      render: (v) => v > 0 ? formatINR(v) : <Text type="secondary">—</Text> },
    { title: 'Credit (₹)', dataIndex: 'creditAmount', key: 'credit', width: 130, align: 'right',
      render: (v) => v > 0 ? formatINR(v) : <Text type="secondary">—</Text> },
    { title: 'Running (₹)', dataIndex: 'runningBalance', key: 'running', width: 150, align: 'right',
      render: (v) => {
        const n = Number(v) || 0;
        const side = n > 0 ? 'Dr' : n < 0 ? 'Cr' : '';
        return (
          <span>
            {formatINR(Math.abs(n))}
            {side ? <Tag style={{ marginInlineStart: 6 }} color={side === 'Dr' ? 'blue' : 'magenta'}>{side}</Tag> : null}
          </span>
        );
      } }
  ]), []);

  const csvColumns = useMemo(() => ([
    { title: 'Date',       value: (r) => shortDate(r.voucherDate) },
    { title: 'Voucher No', dataIndex: 'voucherNo' },
    { title: 'Type',       dataIndex: 'voucherType' },
    { title: 'Narration',  value: (r) => r.lineNarration || r.narration || '' },
    { title: 'Debit',      dataIndex: 'debitAmount' },
    { title: 'Credit',     dataIndex: 'creditAmount' },
    { title: 'Running',    dataIndex: 'runningBalance' }
  ]), []);

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <Title level={4} style={{ margin: 0 }}>Ledger Statement</Title>
        <Space>
          <Button size="small" icon={<ReloadOutlined />} onClick={fetchReport} loading={loading} disabled={!ledgerId}>Refresh</Button>
          <Button size="small" icon={<PrinterOutlined />} onClick={() => window.print()}>Print</Button>
          <Button size="small" icon={<DownloadOutlined />} disabled={!rows.length}
            onClick={() => downloadCsv(`ledger-${ledger?.accountCode || 'statement'}.csv`, rows, csvColumns)}>
            Export CSV
          </Button>
        </Space>
      </div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
        Voucher-line history for a single ledger, with opening, running and closing balances.
      </div>

      <Card size="small" className="erp-accounts-filter" style={{ marginBottom: 10 }}>
        <Space wrap>
          <Space>
            <Text>Ledger:</Text>
            <LedgerPicker value={ledgerId} onChange={setLedgerId} />
          </Space>
          <Space>
            <Text>Date range:</Text>
            <RangePicker size="small" value={dateRange} onChange={setDateRange}
              presets={[
                { label: 'This month',  value: [dayjs().startOf('month'), dayjs()] },
                { label: 'Last 30 days', value: [dayjs().subtract(30, 'day'), dayjs()] },
                { label: 'This FY (Apr–Mar)', value: (function () {
                  const today = dayjs();
                  const yearStart = today.month() >= 3
                    ? dayjs(`${today.year()}-04-01`)
                    : dayjs(`${today.year() - 1}-04-01`);
                  return [yearStart, today];
                })() }
              ]}
              allowClear />
          </Space>
        </Space>
      </Card>

      {loadError && ledgerId ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 10 }}
          message="Could not load Ledger Statement"
          description={friendlyAccountsErrorMessage(loadError, 'ledger_statement')}
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={fetchReport}>
              Retry
            </Button>
          }
        />
      ) : null}

      {!ledgerId ? (
        <Card size="small"><Empty description="Select a ledger to view its statement." /></Card>
      ) : (
        <>
          {ledger ? (
            <Card size="small" className="erp-accounts-filter" style={{ marginBottom: 10 }}>
              <Space split={<span style={{ color: '#e2e8f0' }}>|</span>} wrap>
                <span>
                  <Text type="secondary">Account:</Text>{' '}
                  <code style={{ fontSize: 12 }}>{ledger.accountCode}</code>{' '}
                  <strong>{ledger.accountName}</strong>
                </span>
                <Tag color={ACCOUNT_TYPE_COLOR[ledger.accountType] || 'default'}>
                  {ACCOUNT_TYPE_LABEL[ledger.accountType] || ledger.accountType}
                </Tag>
                {ledger.accountGroup ? <Text type="secondary">{ledger.accountGroup}</Text> : null}
                <Text type="secondary">Normal: {ledger.normalBalance}</Text>
              </Space>
            </Card>
          ) : null}

          <Row gutter={[10, 10]} style={{ marginBottom: 10 }}>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="Opening Balance (₹)" value={Math.abs(summary.openingBalance)}
                  precision={2}
                  suffix={summary.openingBalance > 0 ? 'Dr' : summary.openingBalance < 0 ? 'Cr' : ''} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="Total Debit (₹)" value={summary.totalDebit} precision={2} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="Total Credit (₹)" value={summary.totalCredit} precision={2} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="Closing Balance (₹)" value={closingDisplay.value}
                  precision={2}
                  suffix={closingDisplay.suffix} />
              </Card>
            </Col>
          </Row>

          <Table
            size="small"
            rowKey={(r) => r.lineId || `${r.voucherId}-${r.lineNo}`}
            loading={loading}
            columns={columns}
            dataSource={rows}
            pagination={{ pageSize: 50, size: 'small', showSizeChanger: true, pageSizeOptions: ['25', '50', '100', '200'] }}
            locale={{ emptyText:
              <Empty description={friendlyAccountsEmptyMessage('ledger_statement')} /> }}
          />
        </>
      )}
      </div>
    </ErpMasterShell>
  );
}
